import { useState, useCallback, useRef } from 'react';
import { useAuth } from '../../../../AuthProvider';
import { searchDataBlocks, queryEsriLayer, fetchEsriToken } from '../services/agentApi';

const US_STATES = {
  'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR',
  'california': 'CA', 'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE',
  'florida': 'FL', 'georgia': 'GA', 'hawaii': 'HI', 'idaho': 'ID',
  'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA', 'kansas': 'KS',
  'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
  'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS',
  'missouri': 'MO', 'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV',
  'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
  'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH', 'oklahoma': 'OK',
  'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT',
  'vermont': 'VT', 'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV',
  'wisconsin': 'WI', 'wyoming': 'WY',
};
const STATE_ABBREV_SET = new Set(Object.values(US_STATES));

// Words to strip before sending to Elasticsearch — these confuse the multi-match query
const SEARCH_STOPWORDS = new Set([
  'which', 'what', 'where', 'when', 'how', 'show', 'find', 'get', 'give', 'tell',
  'the', 'a', 'an', 'is', 'are', 'was', 'were', 'has', 'have', 'had', 'do', 'does',
  'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'from', 'and', 'or', 'but',
  'not', 'no', 'its', 'it', 'this', 'that', 'me', 'my', 'we', 'our', 'you',
  'highest', 'lowest', 'most', 'least', 'best', 'worst', 'top', 'bottom',
  'state', 'county', 'tract', 'block', 'zip', 'area', 'region', 'city',
  'indiana', 'california', 'texas', 'florida', 'ohio', 'illinois', 'michigan',
  'georgia', 'pennsylvania', 'washington',
]);

function extractSearchTerms(query) {
  return query
    .toLowerCase()
    .replace(/[?!.,]/g, '')
    .split(/\s+/)
    .filter(w => w.length > 2 && !SEARCH_STOPWORDS.has(w))
    .join(' ')
    || query; // fallback to original if all stripped
}

function parseQuery(query) {
  const q = query.toLowerCase();

  // Geography level
  let layerType = null;
  if (/\bblock group\b/.test(q)) layerType = 'block_group';
  else if (/\bblock\b/.test(q)) layerType = 'block';
  else if (/\btract\b/.test(q)) layerType = 'tract';
  else if (/\bzip\b/.test(q)) layerType = 'zip';
  else if (/\bcounty\b/.test(q)) layerType = 'county';
  else if (/\bstate\b/.test(q) && !/\bstatewide\b/.test(q)) layerType = 'state';

  // Operation
  const orderDirection = /lowest|least|minimum|min|bottom/i.test(q) ? 'ASC' : 'DESC';

  // State detection — multi-word first
  let stateAbbrev = null;
  const sortedNames = Object.keys(US_STATES).sort((a, b) => b.length - a.length);
  for (const name of sortedNames) {
    if (q.includes(name)) {
      stateAbbrev = US_STATES[name];
      break;
    }
  }
  // Also check uppercase abbreviations
  if (!stateAbbrev) {
    const match = query.match(/\b([A-Z]{2})\b/);
    if (match && STATE_ABBREV_SET.has(match[1])) {
      stateAbbrev = match[1];
    }
  }

  const whereClause = stateAbbrev ? `ST_ABBREV='${stateAbbrev}'` : '1=1';

  // Concept tags for display
  const tags = [];
  if (layerType) tags.push({ label: layerType.replace('_', ' '), color: '#4FA490' });
  if (stateAbbrev) tags.push({ label: stateAbbrev, color: '#3B82F6' });
  tags.push({ label: orderDirection === 'DESC' ? 'Highest first' : 'Lowest first', color: '#8B5CF6' });

  return { layerType, orderDirection, stateAbbrev, whereClause, tags };
}

function getUrlYear(url) {
  const match = url.match(/Boundaries_(\d{4})/);
  return match ? parseInt(match[1]) : 0;
}

function pickBestBlocks(hits, layerType, maxResults = 4) {
  // Remove string fields — can't rank by them
  let candidates = hits.filter(h => h._source.fieldType !== 'esriFieldTypeString');

  // Sort by URL year (newest first) — prefer 2024 > 2022 > 2021
  candidates.sort((a, b) => getUrlYear(b._source.layerUrl) - getUrlYear(a._source.layerUrl));

  // Deduplicate by fieldName+layerType — keep newest per combo
  const seen = new Map();
  for (const hit of hits) {
    const key = `${hit._source.fieldName}__${hit._source.layerType}`;
    if (!seen.has(key)) seen.set(key, hit);
  }
  candidates = Array.from(seen.values());
  candidates.sort((a, b) => getUrlYear(b._source.layerUrl) - getUrlYear(a._source.layerUrl));

  // Filter by layerType — but fall back to all if not enough 2024 matches
  if (layerType) {
    const byType = candidates.filter(h => h._source.layerType === layerType);
    const byType2024 = byType.filter(h => getUrlYear(h._source.layerUrl) >= 2023);
    if (byType2024.length > 0) {
      // Good: have recent services at the requested geography level
      return byType2024.slice(0, maxResults);
    } else if (byType.length > 0) {
      // Fallback: have services but archived — include them + 2024 alternatives
      const others2024 = candidates.filter(
        h => getUrlYear(h._source.layerUrl) >= 2023 && h._source.layerType !== layerType,
      );
      return [...byType, ...others2024].slice(0, maxResults);
    }
    // No services for that layerType — use all 2024 results
    return candidates.filter(h => getUrlYear(h._source.layerUrl) >= 2023).slice(0, maxResults);
  }

  // No layerType filter — prefer 2024, fallback to anything
  const recent = candidates.filter(h => getUrlYear(h._source.layerUrl) >= 2023);
  if (recent.length > 0) return recent.slice(0, maxResults);
  return candidates.slice(0, maxResults);
}

function formatValue(value, fieldAlias) {
  if (value === null || value === undefined) return 'N/A';
  const alias = (fieldAlias || '').toLowerCase();
  if (/income|value|price|rent|salary|wage/.test(alias)) {
    return `$${Number(value).toLocaleString()}`;
  }
  if (/rate|growth|percent|%/.test(alias)) {
    return `${Number(value).toFixed(2)}%`;
  }
  if (/index|score/.test(alias)) {
    return Number(value).toFixed(1);
  }
  return Number(value).toLocaleString();
}

export function useGeneralQueryPipeline() {
  const { getAccessToken } = useAuth();
  const [status, setStatus] = useState('idle'); // idle | running | complete | error
  const [parsedQuery, setParsedQuery] = useState(null);
  const [steps, setSteps] = useState([]);
  const [queryResults, setQueryResults] = useState([]);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [esriToken, setEsriToken] = useState(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  const updateStep = useCallback((id, patch) => {
    setSteps(prev => prev.map(s => (s.id === id ? { ...s, ...patch } : s)));
  }, []);

  const reset = useCallback(() => {
    setStatus('idle');
    setParsedQuery(null);
    setSteps([]);
    setQueryResults([]);
    setElapsedSeconds(0);
    setEsriToken(null);
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  const startQuery = useCallback(
    async query => {
      reset();
      setStatus('running');

      // Start elapsed timer
      startTimeRef.current = Date.now();
      timerRef.current = setInterval(() => {
        setElapsedSeconds(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);

      try {
        // --- Phase 1: Parse query ---
        const parseStep = { id: 'parse', label: 'Analyzing query', status: 'running', sublabel: null };
        const tokenStep = { id: 'token', label: 'Fetching ESRI access token', status: 'running', sublabel: null };
        const searchStep = { id: 'search', label: 'Searching ESRI data catalog', status: 'pending', sublabel: null };
        setSteps([parseStep, tokenStep, searchStep]);

        const parsed = parseQuery(query);
        setParsedQuery({ ...parsed, query });
        updateStep('parse', { status: 'complete', sublabel: `${parsed.tags.map(t => t.label).join(' · ')}` });

        // --- Phase 1b: Fetch ESRI token (parallel with parse) ---
        const accessToken = await getAccessToken();
        let fetchedEsriToken = '';
        try {
          fetchedEsriToken = await fetchEsriToken(accessToken);
          setEsriToken(fetchedEsriToken);
          updateStep('token', { status: 'complete', sublabel: 'Ready' });
        } catch {
          updateStep('token', { status: 'error', sublabel: 'Token unavailable — some results may fail' });
        }
        const esriToken = fetchedEsriToken;

        // --- Phase 2: Elasticsearch ---
        const searchTerms = extractSearchTerms(query);
        updateStep('search', { status: 'running', sublabel: `"${searchTerms}"` });
        const hits = await searchDataBlocks(searchTerms);
        const bestBlocks = pickBestBlocks(hits, parsed.layerType);

        if (bestBlocks.length === 0) {
          updateStep('search', { status: 'error', sublabel: 'No matching data fields found' });
          setStatus('complete');
          clearInterval(timerRef.current);
          return;
        }

        updateStep('search', {
          status: 'complete',
          sublabel: `Found ${bestBlocks.length} data field${bestBlocks.length > 1 ? 's' : ''}`,
        });

        // --- Phase 3: Parallel ESRI queries ---
        const fetchSteps = bestBlocks.map((block, i) => ({
          id: `fetch_${i}`,
          label: block._source.fieldAlias,
          sublabel: `${block._source.layerType} · ${block._source.layerTitle}`,
          status: 'running',
        }));
        setSteps(prev => [...prev, ...fetchSteps]);

        const results = await Promise.allSettled(
          bestBlocks.map(async (block, i) => {
            const src = block._source;
            try {
              const data = await queryEsriLayer(
                src.layerUrl,
                src.fieldName,
                parsed.whereClause,
                parsed.orderDirection,
                esriToken,
              );
              const features = data.features || [];
              const formattedFeatures = features.map(f => ({
                name: f.attributes.NAME || f.attributes.name || 'Unknown',
                value: f.attributes[src.fieldName],
                displayValue: formatValue(f.attributes[src.fieldName], src.fieldAlias),
                stateAbbrev: f.attributes.ST_ABBREV,
              }));
              updateStep(`fetch_${i}`, {
                status: 'complete',
                sublabel: `${src.layerType} · ${formattedFeatures.length} result${formattedFeatures.length !== 1 ? 's' : ''}`,
              });
              return {
                fieldAlias: src.fieldAlias,
                fieldName: src.fieldName,
                layerType: src.layerType,
                layerTitle: src.layerTitle,
                mapTitle: src.mapTitle,
                layerUrl: src.layerUrl,
                features: formattedFeatures,
                artifact: {
                  title: src.fieldAlias,
                  group: src.layerTitle,
                  outfields: [src.fieldName],
                  serviceURL: src.layerUrl,
                  id: `gq_${Date.now()}_${i}`,
                  topResultName: formattedFeatures[0]?.name || null,
                },
              };
            } catch (err) {
              updateStep(`fetch_${i}`, { status: 'error', sublabel: err.message });
              return null;
            }
          }),
        );

        const successfulResults = results
          .filter(r => r.status === 'fulfilled' && r.value !== null)
          .map(r => r.value);

        setQueryResults(successfulResults);
        setStatus('complete');
      } catch (err) {
        console.error('General query pipeline error:', err);
        setStatus('error');
      } finally {
        clearInterval(timerRef.current);
      }
    },
    [reset, updateStep, getAccessToken],
  );

  return {
    status,
    parsedQuery,
    steps,
    queryResults,
    elapsedSeconds,
    esriToken,
    startQuery,
    reset,
  };
}
